import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Pause, Play, Sparkle, Stop } from '@phosphor-icons/react';
import GameScore from '../../../shared/components/GameScore';
import { DRUMS } from '../../../shared/data/constants';
import { SCORE_ABC, parseAbcMeasures } from '../../../shared/data/scoreData';
import { getDifficultyData, getFormatLabel } from '../../../shared/utils/drumHelpers';
import {
  drumSelectedSongAtom,
  drumDifficultyAtom,
  drumGameResultAtom,
  drumRecordsAtom,
  drumUserPreferencesAtom,
  drumUserTokenAtom,
} from '../../../shared/store/drumAppAtoms';
import { useDrumMidi } from '../../../shared/store/MidiContext';
import { MIDI_DRUM_INDEX } from '../../../shared/hooks/useMidiDrums';
import type { GameStats } from '../../../shared/data/performanceAnalysis';
import { savePlayRecord } from '../../../shared/api/memberApi';
import './Gameplay.scss';

let judgmentAudioContext: AudioContext | null = null;
const JUDGMENT_RANK = ['Great', 'Good', 'Bad', 'Miss'];

function playJudgmentTone(result: string) {
  const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return;
  if (!judgmentAudioContext) judgmentAudioContext = new AudioContextClass();
  if (judgmentAudioContext.state === 'suspended') {
    judgmentAudioContext.resume().catch(() => undefined);
  }
  const context = judgmentAudioContext;
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const frequencies: Record<string, number> = { Great: 880, Good: 660, Bad: 330, Miss: 196 };
  oscillator.type = 'sine';
  oscillator.frequency.value = frequencies[result] || 520;
  gain.gain.value = 0.07;
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.09);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.1);
}

function judgeTiming(offsetMs: number, interval: number) {
  const ratio = Math.abs(offsetMs) / Math.max(1, interval);
  if (ratio <= 0.22) return 'Great';
  if (ratio <= 0.4) return 'Good';
  return 'Bad';
}

function worseJudgment(left: string, right: string) {
  return JUDGMENT_RANK[Math.max(JUDGMENT_RANK.indexOf(left), JUDGMENT_RANK.indexOf(right))] || 'Bad';
}

function Gameplay() {
  const [song, setSelectedSong] = useAtom(drumSelectedSongAtom);
  const difficulty = useAtomValue(drumDifficultyAtom);
  const setGameResult = useSetAtom(drumGameResultAtom);
  const [, setRecords] = useAtom(drumRecordsAtom);
  const preferences = useAtomValue(drumUserPreferencesAtom);
  const token = useAtomValue(drumUserTokenAtom);
  const midi = useDrumMidi();
  const navigate = useNavigate();

  const [countdown, setCountdown] = useState(4);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [judgment, setJudgment] = useState('READY');
  const [judgmentTick, setJudgmentTick] = useState(0);
  const [stats, setStats] = useState<GameStats>({ Great: 0, Good: 0, Bad: 0, Miss: 0 });
  const [mistakes, setMistakes] = useState<Record<string, number>>(() => Object.fromEntries(DRUMS.map(([name]) => [name, 0])));
  const [paused, setPaused] = useState(false);
  const finishedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const handledMidiAt = useRef<number | null>(null);
  const indexRef = useRef(0);
  const comboRef = useRef(0);
  const judgedRef = useRef<Set<number>>(new Set());
  const hitsByBeatRef = useRef<Map<number, Map<number, number>>>(new Map());
  const countdownRef = useRef(countdown);
  const pausedRef = useRef(paused);
  const judgmentSoundRef = useRef(preferences.judgmentSound);
  const gameBeatsRef = useRef<{ expected: number[]; step: number }[]>([]);
  const beatIntervalRef = useRef(360);
  const elapsedRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const hideJudgmentRef = useRef(0);

  const difficultyData = song ? getDifficultyData(song, difficulty) : null;
  const abcSource = difficultyData?.abcText || SCORE_ABC[difficultyData?.abcKey || 'Easy'] || '';
  const measures = useMemo(() => parseAbcMeasures(abcSource), [abcSource]);
  const gameBeats = useMemo(() => measures.flatMap((measure) => Array.from({ length: measure.slots }, (_, position) => {
    const expected = [...new Set(measure.events.filter((event) => event.position === position && !event.rest && Number.isInteger(event.drumIndex)).map((event) => event.drumIndex as number))];
    return { expected, step: measure.startStep + position };
  })), [measures]);
  const beatInterval = Math.round(60000 / Math.max(60, song?.bpm || 93));

  indexRef.current = index;
  comboRef.current = combo;
  countdownRef.current = countdown;
  pausedRef.current = paused;
  judgmentSoundRef.current = preferences.judgmentSound;
  gameBeatsRef.current = gameBeats;
  beatIntervalRef.current = beatInterval;

  useEffect(() => {
    if (!song) navigate('/drum/library', { replace: true });
  }, [song, navigate]);

  useEffect(() => () => window.clearTimeout(hideJudgmentRef.current), []);

  const resolveBeat = useCallback((beatIndex: number, result: string) => {
    if (finishedRef.current || judgedRef.current.has(beatIndex)) return;
    const beats = gameBeatsRef.current;
    if (beatIndex < 0 || beatIndex >= beats.length) return;
    judgedRef.current.add(beatIndex);
    const expectedDrums = beats[beatIndex]?.expected || [];
    const points = { Great: 1000, Good: 650, Bad: 200, Miss: 0 }[result] ?? 0;
    if (result === 'Bad' || result === 'Miss') {
      setMistakes((value) => {
        const next = { ...value };
        expectedDrums.forEach((expectedDrum) => {
          const expectedName = DRUMS[expectedDrum]?.[0];
          if (expectedName) next[expectedName] = (next[expectedName] || 0) + 1;
        });
        return next;
      });
    }
    setJudgment(result.toUpperCase());
    setJudgmentTick((value) => value + 1);
    window.clearTimeout(hideJudgmentRef.current);
    hideJudgmentRef.current = window.setTimeout(() => setJudgmentTick(0), 800);
    if (judgmentSoundRef.current) playJudgmentTone(result);
    setScore((value) => value + points + (result === 'Great' ? comboRef.current * 12 : 0));
    setStats((value) => ({ ...value, [result]: (value as any)[result] + 1 }));
    if (result === 'Bad' || result === 'Miss') {
      comboRef.current = 0;
      setCombo(0);
    } else {
      const nextCombo = comboRef.current + 1;
      comboRef.current = nextCombo;
      setCombo(nextCombo);
      setMaxCombo((best) => Math.max(best, nextCombo));
    }
  }, []);

  const closeBeat = useCallback((beatIndex: number) => {
    if (finishedRef.current || judgedRef.current.has(beatIndex)) return;
    const expected = gameBeatsRef.current[beatIndex]?.expected || [];
    if (!expected.length) {
      judgedRef.current.add(beatIndex);
      return;
    }
    const hits = hitsByBeatRef.current.get(beatIndex) || new Map<number, number>();
    const matched = expected.filter((drum) => hits.has(drum));
    if (!matched.length) {
      resolveBeat(beatIndex, 'Miss');
      return;
    }
    const interval = beatIntervalRef.current;
    let result = matched.reduce((worst, drum) => worseJudgment(worst, judgeTiming(hits.get(drum) ?? interval, interval)), 'Great');
    if (matched.length < expected.length) result = result === 'Great' ? 'Good' : result === 'Good' ? 'Bad' : result;
    resolveBeat(beatIndex, result);
  }, [resolveBeat]);

  const registerHit = useCallback((drum: number) => {
    if (countdownRef.current > 0 || pausedRef.current || finishedRef.current) return;
    const beats = gameBeatsRef.current;
    const interval = Math.max(1, beatIntervalRef.current);
    const elapsed = elapsedRef.current;
    let beatIndex = Math.floor(elapsed / interval);
    const frac = elapsed - beatIndex * interval;
    if (beatIndex < beats.length && judgedRef.current.has(beatIndex) && frac > interval * 0.72) {
      beatIndex += 1;
    }
    if (beatIndex < 0 || beatIndex >= beats.length || judgedRef.current.has(beatIndex)) return;
    const expected = beats[beatIndex]?.expected || [];
    if (!expected.length || !expected.includes(drum)) {
      resolveBeat(beatIndex, 'Bad');
      return;
    }
    resolveBeat(beatIndex, judgeTiming(elapsed - beatIndex * interval, interval));
  }, [resolveBeat]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 850);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (countdown === 0 && !startedAtRef.current) startedAtRef.current = Date.now();
  }, [countdown]);

  useEffect(() => {
    if (countdown > 0 || paused || finishedRef.current || !song) return undefined;
    lastTickRef.current = null;
    let raf = 0;
    const tick = (now: number) => {
      if (finishedRef.current) return;
      if (lastTickRef.current != null) elapsedRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      const beats = gameBeatsRef.current;
      const timelineIndex = Math.min(beats.length, Math.floor(elapsedRef.current / Math.max(1, beatIntervalRef.current)));
      for (let beatIndex = 0; beatIndex < timelineIndex; beatIndex += 1) {
        if (!judgedRef.current.has(beatIndex)) closeBeat(beatIndex);
      }
      if (indexRef.current !== timelineIndex) {
        indexRef.current = timelineIndex;
        setIndex(timelineIndex);
      }
      if (timelineIndex < beats.length) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      lastTickRef.current = null;
      window.cancelAnimationFrame(raf);
    };
  }, [countdown, paused, song, closeBeat]);

  useEffect(() => {
    if (countdown > 0 || paused || finishedRef.current || !song || index < gameBeats.length) return undefined;
    finishedRef.current = true;
    const total = Math.max(1, Object.values(stats).reduce((sum, value) => sum + value, 0));
    const finishTimer = window.setTimeout(() => {
      const accuracy = Math.round(((stats.Great + stats.Good * 0.7 + stats.Bad * 0.25) / total) * 100);
      const rank = accuracy >= 90 ? 'A+' : accuracy >= 80 ? 'A' : 'B';
      const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      const localRecord = {
        playId: `play-${Date.now()}`,
        scoreId: song.songId,
        title: song.title,
        difficulty,
        score,
        rank,
        accuracy,
        maxCombo,
        playedAt: new Date().toISOString(),
        durationMs,
        stats,
        mistakes,
      };
      setGameResult({ score, maxCombo, stats, mistakes, accuracy, playId: localRecord.playId, saveStatus: token ? 'saving' : 'saved' });
      navigate('/drum/results');

      const applyRecord = (record: typeof localRecord, saveStatus: 'saved' | 'failed') => {
        setGameResult((current) => (current?.playId === localRecord.playId || current?.playId === record.playId
          ? { ...current, playId: record.playId, saveStatus }
          : current));
        setRecords((current) => (current.some((item) => item.playId === record.playId) ? current : [record, ...current]));
      };

      if (!token) {
        window.setTimeout(() => applyRecord(localRecord, 'saved'), 400);
        return;
      }

      savePlayRecord(token, {
        scoreId: Number(song.songId),
        title: song.title,
        difficulty,
        score,
        accuracy,
        maxCombo,
        rank,
        durationMs,
        stats,
        mistakes,
      }).then((saved) => {
        applyRecord({ ...localRecord, ...saved, difficulty, stats: saved.stats || stats, mistakes: saved.mistakes || mistakes }, 'saved');
      }).catch(() => {
        applyRecord(localRecord, 'failed');
      });
    }, 500);
    return () => window.clearTimeout(finishTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, paused, index, gameBeats.length, song]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.volume = 0.46;
    if (countdown > 0 || paused || finishedRef.current) {
      audio.pause();
      return undefined;
    }
    const offset = song?.audioStartOffset || 0;
    if (offset > 0 && audio.currentTime < offset) audio.currentTime = offset;
    const tryPlay = () => audio.play().catch(() => undefined);
    tryPlay();
    audio.addEventListener('canplay', tryPlay);
    return () => {
      audio.removeEventListener('canplay', tryPlay);
      audio.pause();
    };
  }, [countdown, paused, song]);

  useEffect(() => {
    if (!midi.lastHit || handledMidiAt.current === midi.lastHit.at) return;
    handledMidiAt.current = midi.lastHit.at;
    if (countdownRef.current > 0 || pausedRef.current) return;
    const drum = MIDI_DRUM_INDEX[midi.lastHit.drumId];
    if (drum === undefined) return;
    registerHit(drum);
  }, [midi.lastHit, registerHit]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const drum = DRUMS.findIndex((item) => (item[2] === 'Space' ? event.code === 'Space' : item[2].toLowerCase() === event.key.toLowerCase()));
      if (drum >= 0) registerHit(drum);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [registerHit]);

  if (!song) return null;

  const exitToLibrary = () => {
    setSelectedSong(null);
    setGameResult(null);
    navigate('/drum/library');
  };

  return (
    <main className="gameplay-screen" data-localized-root>
      <audio ref={audioRef} src={song.playAudio} preload="auto" data-game-audio />
      <header className="game-hud">
        <div><span>{song.title}</span><small>{difficulty} · {getFormatLabel(song, difficulty)}</small></div>
        <div className="hud-score">
          <span>SCORE<strong>{score.toLocaleString()}</strong></span>
          <span>COMBO<strong>{combo}</strong></span>
          <button className="icon-button" onClick={() => setPaused(!paused)} aria-label={paused ? 'Resume stage' : 'Pause stage'}>{paused ? <Play weight="fill" /> : <Pause weight="fill" />}</button>
        </div>
      </header>
      <section className="stage-area">
        <img className="stage-backdrop" src={song.thumbnail} alt="" />
        <div className="stage-tint" />
        <GameScore difficulty={difficulty} current={index} measures={measures} />
        {countdown <= 0 && judgmentTick > 0 && (
          <div key={`${judgment}-${judgmentTick}`} className={`judgment ${judgment.toLowerCase()}`}>
            <Sparkle className="judgment-spark left" weight="fill" /><span>{judgment}</span><Sparkle className="judgment-spark right" weight="fill" />
            <small>{combo > 1 ? `${combo} COMBO` : 'KEEP THE BEAT'}</small>
          </div>
        )}
        {judgmentTick > 0 && <div key={`hit-${judgmentTick}`} className={`hit-flash ${judgment.toLowerCase()}`} />}
        {countdown > 0 && (
          <div key={countdown} className={`countdown ${countdown === 4 ? 'ready' : ''}`}>
            <strong>{countdown === 4 ? 'READY' : countdown}</strong><small>{countdown === 4 ? 'GET SET' : 'FOLLOW THE BEAT'}</small>
          </div>
        )}
        {paused && (
          <div className="pause-panel" role="dialog" aria-modal="true" aria-labelledby="pause-dialog-title">
            <Pause weight="fill" /><h2 id="pause-dialog-title">Paused</h2>
            <button onClick={() => { setCountdown(3); setPaused(false); }}><Play weight="fill" /> Resume</button>
            <button onClick={exitToLibrary}><Stop weight="fill" /> Leave Stage</button>
          </div>
        )}
      </section>
    </main>
  );
}

export default Gameplay;
